import React, {CSSProperties} from "react";
import {primaryText, secondaryBackground} from "../../colors";
import { Tooltip as ToolTip, Button } from "@octopusdeploy/design-system-components";
import { XmarkIcon } from "@octopusdeploy/design-system-icons";
import {normal} from "../../fontWeights";
import { LocationDescriptor } from "history";
import InternalLink from "components/Navigation/InternalLink/InternalLink";
import {avatarStyles, CommonChipProps} from ".";
import styles = require("./styles.less");

interface ExtraCommonProps {
    description?: string;
    backgroundColor?: string;
    markAsRemoved?: boolean;
    labelColor?: string;
    children?: any;
}

interface IconStyles {
    margin: number;
    fill: string;
    width: number;
    height: number;
    backgroundColor?: string;
}

interface ChipProps {
    className?: string;
    description?: string;
    onRequestDelete?: (event: object) => void;
    onClick?: (event: object) => void;
    backgroundColor?: string;
    labelColor?: string;
    markAsRemoved?: boolean;
    tabIndex?: number;
    avatar?: React.ReactNode;
    bordercolor?: string;
    style?: object;
    fullWidth?: boolean;
    noMargin?: boolean;
    to?: LocationDescriptor;
    children?: React.ReactNode;
}

const Chip: React.FC<ChipProps> = props => {
    const {
        className,
        description,
        onRequestDelete,
        onClick,
        backgroundColor = secondaryBackground,
        labelColor = primaryText,
        markAsRemoved,
        tabIndex = -1,
        avatar,
        bordercolor,
        style,
        fullWidth,
        noMargin,
        children,
    } = props;

    const containerStyle: CSSProperties = {
        display: "inline-flex",
        alignItems: "center",
        height: "1.5rem",
        lineHeight: "1.5rem",
        margin: noMargin ? 0 : 4,
        padding: "0 4px",
        borderRadius: "1rem",
        backgroundColor,
        borderWidth: bordercolor ? "0.0625rem" : 0,
        borderStyle: bordercolor ? "solid" : "none",
        borderColor: bordercolor || "transparent",
        cursor: onClick ? "pointer" : "inherit",
        verticalAlign: "middle",
        ...style,
    };

    const labelStyle: CSSProperties = {
        lineHeight: "1.5rem",
        fontSize: "0.75rem",
        color: labelColor,
        fontWeight: normal,
        maxWidth: fullWidth ? "none" : "12.5rem",
        textDecoration: markAsRemoved ? "line-through" : "none",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        padding: "0 4px",
    };

    const chipBody = (
        <span
            className={className}
            style={containerStyle}
            tabIndex={tabIndex}
            onClick={onClick as any}
            role={onClick ? "button" : undefined}
        >
            {avatar && <span style={{display: "inline-flex", alignItems: "center", marginLeft: -4}}>{avatar}</span>}
            <span style={labelStyle}>{children}</span>
            {onRequestDelete && (
                <span
                    onClick={(e) => e.stopPropagation()}
                    style={{display: "inline-flex", alignItems: "center", marginRight: 4}}
                >
                    <Button
                        importance="ghost"
                        size="small"
                        icon={<XmarkIcon size={16} />}
                        onClick={(e) => { e.stopPropagation(); onRequestDelete(e); }}
                        accessibleName="Delete"
                    />
                </span>
            )}
        </span>
    );

    return <ToolTip content={<span>{description || children}</span>}>{chipBody}</ToolTip>;
};

const chipWithAvatar = (
    text: string,
    SvgIcon: any,
    props: CommonChipProps & ExtraCommonProps,
    customAvatar?: any,
    description?: string,
    labelColor?: string,
    customIconStyles?: IconStyles,
    fullWidth = false) => {
    const iconElement = <SvgIcon style={customIconStyles || {margin: 5, fill: primaryText, width: 14, height: 14}} />;
    const avatarElement = (
        <span style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: avatarStyles.width,
            height: avatarStyles.height,
            borderRadius: "50%",
            ...(customAvatar || {}),
        }}>
            {iconElement}
        </span>
    );

    const theChip = <Chip backgroundColor={secondaryBackground}
                          fullWidth={fullWidth}
                          labelColor={labelColor || primaryText}
                          description={description || text}
                          avatar={avatarElement}
                          {...props}>
        {text}
    </Chip>;
    if (props.to) {
        return <InternalLink to={props.to} className={styles.clickableChip}>{theChip}</InternalLink>;
    }
    return theChip;
};

export default Chip;
export {ChipProps, chipWithAvatar};
